import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from '@client-app/i18n';
import { X } from 'lucide-react';
import React from 'react';
import { createPortal } from 'react-dom';
import PlanSelector, { type UserPlanInfo } from './PlanSelector';
import styles from './PricingModal.module.scss';
import { lockDocumentScroll } from './pricingScrollLock';
import type { SelectablePlanCode } from './teamOfferSelection';
import PluginPlanSelector from './PluginPlanSelector';
import { getCurrentProductCode, isPluginProduct } from '@common/api/productContext';

interface PricingModalProps {
    isOpen: boolean;
    onClose: () => void;
    fisrt?: boolean;
    workspaceId?: string;
    preferredTeamOfferCode?: SelectablePlanCode | null;
}

export const PricingModal: React.FC<PricingModalProps> = ({
    isOpen, onClose, fisrt, workspaceId, preferredTeamOfferCode
}) => {
    const { t } = useTranslation();
    const {
        userBilling: { userPlan },
        currentWorkspace
    } = useAuth();
    const shouldUsePluginPricing = isPluginProduct(getCurrentProductCode());
    const resolvedWorkspaceId = workspaceId || currentWorkspace?.id || '';

    React.useEffect(() => {
        if (!isOpen) return;
        return lockDocumentScroll(document);
    }, [isOpen]);

    if (!isOpen) return null;

    const userPlanInfo: UserPlanInfo = {
        planCode: userPlan?.planCode,
        billingCycle: userPlan?.billingCycle as 'monthly' | 'yearly' | undefined,
        status: userPlan?.status,
        currentPeriodEnd: userPlan?.currentPeriodEnd,
        trialEndAt: userPlan?.trialEndAt
    };

    return createPortal(
        <div className={styles.modalOverlay} onClick={onClose}>
            <div className={styles.centeredWrapper}>
                <div
                    className={styles.modalContainer}
                    onClick={(e) => e.stopPropagation()}
                >
                    <button
                        onClick={onClose}
                        className={styles.closeButton}
                        aria-label={t('common.closeModal')}
                    >
                        <X size={24} />
                    </button>

                    <div className={styles.header}>
                        <h2 className={styles.title}>
                            {t(shouldUsePluginPricing ? 'website.pricing.pluginTitle' : 'website.pricing.title')}
                        </h2>
                    </div>

                    <div className={styles.cardsContainer}>
                        {shouldUsePluginPricing ? (
                            <PluginPlanSelector workspaceId={resolvedWorkspaceId} />
                        ) : (
                            <PlanSelector
                                mode="upgrade"
                                userPlan={userPlanInfo}
                                workspaceId={resolvedWorkspaceId}
                                preferredTeamOfferCode={preferredTeamOfferCode}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

export * from './PaymentSuccessModal';
export * from './Welcome';
